import { c as _c } from "react/compiler-runtime";
import { motion, AnimatePresence, MotionProps, useReducedMotion } from 'framer-motion';
import { PropsWithChildren, useEffect, useId } from 'react';
import FocusLock from 'react-focus-lock';
import CrossIcon from '@/assets/icons/heroIcons/Cross.svg';
import { useKey } from 'react-use';
import { Portal } from '@/components/portal';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { logEvent } from '@/analytics/events';
import { uppercaseFirstLetter } from '@/utils/string';
import { mergeClasses } from '@/utils/style';
import { useLockDocument } from '@/hooks/useLockDocument';
interface Props {
  isVisible: boolean;
  modalName: string;
  heading?: string;
  onClose: () => void;
  analyticsParam: string;
  testId: string;
  className?: string;
  backdropClassName?: string;
  closeButtonClassName?: string;
}
const mainModalAnimation: MotionProps = {
  transition: {
    duration: 0.2,
    delay: 0.1,
    ease: 'easeInOut'
  },
  initial: {
    opacity: 0,
    scale: 0.9,
    y: 20
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
};
const mainBackdropAnimation: MotionProps = {
  transition: {
    duration: 0.2,
    ease: 'easeInOut'
  },
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
};
export function Modal(props) {
  const $ = _c(24);
  const {
    isVisible,
    onClose,
    children,
    heading,
    modalName,
    analyticsParam,
    testId
  } = props;
  const shouldReduceMotion = useReducedMotion();
  const {
    t
  } = useTranslation();
  const headingId = useId();
  let t0;
  if ($[0] !== shouldReduceMotion) {
    t0 = shouldReduceMotion ? {} : mainModalAnimation;
    $[0] = shouldReduceMotion;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const modalAnimation = t0;
  let t1;
  if ($[2] !== shouldReduceMotion) {
    t1 = shouldReduceMotion ? {} : mainBackdropAnimation;
    $[2] = shouldReduceMotion;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const modalBackdropAnimation = t1;
  useKey("Escape", props.onClose);
  useLockDocument(isVisible);
  let t2;
  let t3;
  if ($[4] !== isVisible || $[5] !== modalName) {
    t2 = () => {
      if (isVisible) {
        logEvent("view_modal", {
          modal_name: modalName
        });
      }
    };
    t3 = [isVisible, modalName];
    $[4] = isVisible;
    $[5] = modalName;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== analyticsParam || $[9] !== children || $[10] !== heading || $[11] !== headingId || $[12] !== isVisible || $[13] !== modalAnimation || $[14] !== modalBackdropAnimation || $[15] !== onClose || $[16] !== props.backdropClassName || $[17] !== props.className || $[18] !== props.closeButtonClassName || $[19] !== t || $[20] !== testId) {
    t4 = isVisible && <><FocusLock returnFocus={true}><motion.div key="modal" role="dialog" aria-modal="true" aria-labelledby={headingId} {...modalAnimation} className={mergeClasses("max fixed inset-4 z-60 m-auto flex h-fit max-w-292 flex-col rounded-4xl bg-white p-8", props.className)} data-analytics-param={`modal${uppercaseFirstLetter(analyticsParam)}`} data-test-id={`modal${uppercaseFirstLetter(testId)}`}><Button analyticsParam="modalClose" testId="modalClose" variant="unstyled" type="button" onClick={onClose} className={mergeClasses("absolute top-1 right-1 z-60 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-gray-500 md:top-4 md:right-4", props.closeButtonClassName)}><CrossIcon className="h-7 w-7 lg:h-8 lg:w-8" /><span className="sr-only">{t("close")}</span></Button>{heading && <div><h2 id={headingId}>{heading}</h2></div>}<div>{children}</div></motion.div></FocusLock><motion.div key="modal-backdrop" {...modalBackdropAnimation} onClick={onClose} className={mergeClasses("fixed inset-0 z-50 bg-black/50", props.backdropClassName)} /></>;
    $[8] = analyticsParam;
    $[9] = children;
    $[10] = heading;
    $[11] = headingId;
    $[12] = isVisible;
    $[13] = modalAnimation;
    $[14] = modalBackdropAnimation;
    $[15] = onClose;
    $[16] = props.backdropClassName;
    $[17] = props.className;
    $[18] = props.closeButtonClassName;
    $[19] = t;
    $[20] = testId;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== t4) {
    t5 = <Portal><AnimatePresence>{t4}</AnimatePresence></Portal>;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  return t5;
}